import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth, ROLE_LABELS } from "@/context/AuthContext";
import { NAV_GROUPS, PAGE_TITLES } from "@/config/nav";
import api, { API, TOKEN_KEY } from "@/lib/api";
import { Truck, LogOut, DatabaseBackup, Loader2, Menu } from "lucide-react";
import { toast } from "sonner";

function pageTitle(pathname) {
  if (PAGE_TITLES[pathname]) return PAGE_TITLES[pathname];
  return "TMS";
}

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [backing, setBacking] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const doBackup = async () => {
    setBacking(true);
    try {
      const res = await api.get("/backup", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `tms_backup_${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Backup scaricato");
    } catch {
      toast.error("Errore durante il backup");
    } finally {
      setBacking(false);
    }
  };

  const Sidebar = (
    <aside className="w-64 shrink-0 bg-slate-900 text-slate-300 flex flex-col h-full">
      <div className="h-14 flex items-center gap-2.5 px-4 border-b border-slate-800">
        <div className="h-8 w-8 rounded-md bg-sky-600 flex items-center justify-center">
          <Truck className="h-4.5 w-4.5 text-white" style={{ width: 18, height: 18 }} />
        </div>
        <div>
          <div className="text-sm font-head font-bold text-white leading-none">TMS</div>
          <div className="text-[10px] text-slate-500 mt-0.5">Gestione Trasporti</div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-4">
        {NAV_GROUPS.map((group, gi) => {
          const items = group.items.filter(
            (it) => !it.adminOnly || user?.role === "admin"
          );
          if (!items.length) return null;
          return (
            <div key={gi}>
              {group.label && (
                <div className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                  {group.label}
                </div>
              )}
              <div className="space-y-0.5">
                {items.map((it) => {
                  const Icon = it.icon;
                  return (
                    <NavLink
                      key={it.to}
                      to={it.to}
                      end={it.to === "/"}
                      data-testid={it.testid}
                      onClick={() => setMobileOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors ${
                          isActive
                            ? "bg-sky-600 text-white font-medium"
                            : "hover:bg-slate-800 hover:text-white"
                        }`
                      }
                    >
                      <Icon className="h-4 w-4 shrink-0" />
                      {it.label}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>
      <div className="p-3 border-t border-slate-800 text-[11px] text-slate-500">
        v1.0 · Fase 1
      </div>
    </aside>
  );

  return (
    <div className="h-screen flex overflow-hidden">
      <div className="hidden lg:block">{Sidebar}</div>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 h-full">{Sidebar}</div>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 shrink-0 bg-white border-b border-slate-200 flex items-center justify-between px-4 gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <button
              className="lg:hidden p-1.5 rounded hover:bg-slate-100"
              onClick={() => setMobileOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="h-5 w-5" />
            </button>
            <h1 className="text-lg font-head font-bold text-slate-900 truncate" data-testid="page-title">
              {pageTitle(location.pathname)}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            {user?.role === "admin" && (
              <button
                onClick={doBackup}
                disabled={backing}
                data-testid="backup-btn"
                className="hidden sm:inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-sky-700 border border-slate-200 rounded-md px-2.5 py-1.5 transition-colors"
              >
                {backing ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <DatabaseBackup className="h-3.5 w-3.5" />
                )}
                Esporta backup
              </button>
            )}
            <div className="text-right hidden sm:block">
              <div className="text-sm font-medium text-slate-800 leading-none">
                {user?.nome || user?.username}
              </div>
              <div className="text-[11px] text-sky-700">{ROLE_LABELS[user?.role]}</div>
            </div>
            <button
              onClick={logout}
              data-testid="logout-btn"
              title="Esci"
              className="p-2 rounded-md text-slate-500 hover:bg-slate-100 hover:text-red-600 transition-colors"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </header>
        <main className="flex-1 overflow-auto p-5">{children}</main>
      </div>
    </div>
  );
}
